import moment from 'moment'
import 'moment/locale/es'
import {cuentaMask, dineroDisabledMask} from '../Components/Items'
import anexoAnalisisBalancePdfV2 from './PdfV2.0/anexoAnalisisBalancePdfV2'
import {
    esModerno,
    construirAnaliticas,
    renglonesResumen,
    renglonesRazones,
    formatoVeces,
    formatoPorcentaje,
} from './balanceGeneralAnaliticas'

moment.locale('es')

// Anexo del Estado de Posicion Financiera: los totales, el analisis vertical y las
// razones financieras, en un documento aparte del balance. Se descarga junto con el
// balance cuando la plantilla elegida es una "con analisis completo"; asi la hoja del
// balance se queda siendo solo el arbol de cuentas y el anexo se puede mandar solo.

const layoutAnalisis = {
    hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length ? 1 : 0.5),
    vLineWidth: () => 0,
    hLineColor: () => '#ADAAAA',
    paddingLeft: () => 5,
    paddingRight: () => 5,
    paddingTop: () => 4,
    paddingBottom: () => 4,
}

const anexoAnalisisBalancePdf = (values) => {
    const empresa = values.empresa
    const tipoPdf = values.tipoPdfBalance

    if (esModerno(tipoPdf) && empresa?.logo?.base64Completa) {
        return anexoAnalisisBalancePdfV2(values)
    }

    const analiticas = construirAnaliticas(values)
    const tieneLogo = !!empresa?.logo?.base64Completa

    // Sin analiticas no hay anexo que armar. La pantalla ya no lo pide en ese caso,
    // pero un doc-definition tiene que devolver algo valido de todos modos.
    if (!analiticas) {
        return {
            content: [{text: 'No hay informacion de analisis para este corte.', alignment: 'center'}],
            pageSize: 'LETTER',
            pageMargins: [40, 40, 40, 40],
        }
    }

    const filtrados = []
    if (values.filtrarProyecto === 1) {
        filtrados.push([
            {text: 'Proyectos:', style: 'subheader'},
            {text: (values.proyectos || []).map((p) => p.nombre).join('\n'), style: 'textoChico'},
        ])
    } else if (values.filtrarProyecto === 2 && values.unidadDeNegocio) {
        const udn = values.unidadDeNegocio
        filtrados.push([
            {text: 'Unidad de Negocio:', style: 'subheader'},
            {
                text: cuentaMask(
                    (udn.nivel1 || '') + (udn.nivel2 || '') + (udn.nivel3 || '') +
                    (udn.nivel4 || '') + (udn.nivel5 || '')
                ) + ' - ' + udn.nombre,
                style: 'textoChico',
            },
        ])
    }
    if (filtrados.length === 0) {
        filtrados.push([
            {text: 'Filtros:', style: 'subheader'},
            {text: 'No tiene filtrados adicionales', style: 'textoChico'},
        ])
    }

    const {subtotales} = analiticas

    // El cuadre del balance. La Diferencia debe dar cero; si no, hay cuentas mal
    // clasificadas en el catalogo o polizas descuadradas, y sale en rojo.
    const cuadre = [
        {etiqueta: 'Suma del Activo', valor: subtotales.totalActivo},
        {etiqueta: 'Suma del Pasivo y Capital', valor: subtotales.totalPasivoMasCapital},
        {
            etiqueta: 'Diferencia',
            valor: subtotales.diferencia,
            alerta: subtotales.diferencia !== 0,
        },
    ]

    const dd = {
        info: {
            title: 'Anexo - Analisis del Balance',
            author: 'BIWO ERP',
            subject: 'Anexo de analisis del Estado de Posicion Financiera',
            creator: 'pdfmake',
        },
        content: [
            {
                style: 'tableExample',
                table: {
                    widths: ['18%', '52%', '30%'],
                    body: [[
                        // Sin logo NO se puede emitir el nodo `image`: pdfMake trata la
                        // cadena vacia como una ruta de archivo y truena el PDF entero.
                        tieneLogo
                            ? {border: [false, false, false, false], image: 'logo', fit: [80, 80]}
                            : {border: [false, false, false, false], text: ''},
                        {
                            border: [false, false, false, false],
                            table: {
                                widths: ['100%'],
                                body: [
                                    [{
                                        text: empresa.nombreComercial
                                            ? empresa.nombreComercial
                                            : (empresa.persona
                                                ? empresa.nombre
                                                : empresa.nombre + ' ' + empresa.apellidoPaterno + ' ' + empresa.apellidoMaterno),
                                        style: 'tableHeader',
                                    }],
                                    [{text: 'R.F.C: ' + empresa.rfc, style: 'textoChico', alignment: 'center'}],
                                ],
                            },
                            layout: 'noBorders',
                        },
                        {
                            border: [false, false, false, false],
                            table: {
                                widths: ['100%'],
                                body: [
                                    [{text: 'Fecha Final:', style: 'subheader', alignment: 'center'}],
                                    [{text: moment(values.fechaFinal).locale('es').format('LL'), style: 'textoChico', alignment: 'center'}],
                                    [{text: 'Fecha de Impresión:', style: 'subheader', alignment: 'center'}],
                                    [{text: moment().locale('es').format('LL'), style: 'textoChico', alignment: 'center'}],
                                ],
                            },
                            layout: 'noBorders',
                        },
                    ]],
                },
                layout: 'noBorders',
            },

            {
                style: 'tableExample',
                table: {
                    widths: ['100%'],
                    body: [[{
                        text: 'ANEXO — ANALISIS DEL BALANCE',
                        alignment: 'center',
                        fontSize: 16,
                        border: [false, false, false, false],
                    }]],
                },
                layout: 'noBorders',
            },

            ...(values.ocultarDatosFiltrado ? [] : [
                {text: 'Datos de filtrado:', style: 'subheader', margin: [0, 4, 0, 2]},
                {
                    style: 'tableExample',
                    table: {widths: ['25%', '75%'], body: filtrados},
                    layout: layoutAnalisis,
                },
            ]),

            {text: 'CUADRE DEL BALANCE', style: 'tituloBloque'},
            {
                table: {
                    headerRows: 1,
                    widths: ['*', 'auto'],
                    body: [
                        [
                            {text: 'Concepto', style: 'tituloTabla', alignment: 'left'},
                            {text: 'Importe', style: 'tituloTabla', alignment: 'right'},
                        ],
                        ...cuadre.map((renglon) => {
                            const color = renglon.alerta ? {color: '#CF1322'} : {}
                            return [
                                {text: renglon.etiqueta, style: 'analisisFuerte', alignment: 'left', ...color},
                                {
                                    text: dineroDisabledMask(renglon.valor),
                                    style: 'analisisFuerte',
                                    alignment: 'right',
                                    noWrap: true,
                                    ...color,
                                },
                            ]
                        }),
                    ],
                },
                layout: layoutAnalisis,
            },

            {text: 'INTEGRACION DEL BALANCE', style: 'tituloBloque'},
            {
                table: {
                    headerRows: 1,
                    widths: ['*', 'auto', 'auto'],
                    body: [
                        [
                            {text: 'Concepto', style: 'tituloTabla', alignment: 'left'},
                            {text: 'Importe', style: 'tituloTabla', alignment: 'right'},
                            {text: '% Integración', style: 'tituloTabla', alignment: 'right'},
                        ],
                        ...renglonesResumen(analiticas).map((renglon) => {
                            const style = renglon.fuerte ? 'analisisFuerte' : 'textoChico'
                            const color = renglon.alerta ? {color: '#CF1322'} : {}
                            return [
                                {text: renglon.etiqueta, style, alignment: 'left', ...color},
                                {
                                    text: dineroDisabledMask(analiticas.subtotales[renglon.llave]),
                                    style,
                                    alignment: 'right',
                                    noWrap: true,
                                    ...color,
                                },
                                {
                                    text: formatoPorcentaje(analiticas.porcentajes[renglon.llave]),
                                    style,
                                    alignment: 'right',
                                    noWrap: true,
                                    ...color,
                                },
                            ]
                        }),
                    ],
                },
                layout: layoutAnalisis,
            },

            {text: 'RAZONES FINANCIERAS', style: 'tituloBloque'},
            {
                table: {
                    headerRows: 1,
                    widths: ['auto', '*', 'auto'],
                    body: [
                        [
                            {text: 'Razón', style: 'tituloTabla', alignment: 'left'},
                            {text: 'Fórmula', style: 'tituloTabla', alignment: 'left'},
                            {text: 'Resultado', style: 'tituloTabla', alignment: 'right'},
                        ],
                        ...renglonesRazones(analiticas).map((renglon) => {
                            let texto
                            if (renglon.formato === 'dinero') {
                                texto = renglon.valor === null ? 'N/A' : dineroDisabledMask(renglon.valor)
                            } else if (renglon.formato === 'porcentaje') {
                                texto = formatoPorcentaje(renglon.valor)
                            } else {
                                texto = formatoVeces(renglon.valor)
                            }
                            return [
                                {text: renglon.etiqueta, style: 'analisisFuerte', alignment: 'left', noWrap: true},
                                {text: renglon.nota, style: 'textoNota', alignment: 'left'},
                                {text: texto, style: 'analisisFuerte', alignment: 'right', noWrap: true},
                            ]
                        }),
                    ],
                },
                layout: layoutAnalisis,
            },

            {
                text: 'Las razones se calculan sobre los saldos al corte indicado. "N/A" significa que el divisor es cero.',
                style: 'textoNota',
                margin: [0, 10, 0, 0],
            },
        ],
        ...(tieneLogo ? {images: {logo: empresa.logo.base64Completa}} : {}),
        styles: {
            subheader: {fontSize: 8, bold: true},
            tableExample: {margin: [0, 5, 0, 5]},
            tableHeader: {bold: true, fontSize: 12, color: 'black', alignment: 'center'},
            textoChico: {fontSize: 8},
            textoNota: {fontSize: 7, color: '#666666'},
            tituloTabla: {fontSize: 8, bold: true, fillColor: '#cccccc', alignment: 'center'},
            tituloBloque: {fontSize: 11, bold: true, alignment: 'center', margin: [0, 14, 0, 4]},
            analisisFuerte: {fontSize: 8, bold: true},
        },
        pageSize: 'LETTER',
        pageMargins: [40, 36, 40, 36],
    }

    return dd
}

export default anexoAnalisisBalancePdf
